import AddFavoriteButton from '@/src/components/UI/AddFavoriteButton';
import HomeLoanCalculation from '@/src/components/UI/HouseDetail/HomeLoanCalculation';
import ItemFeature from '@/src/components/UI/HouseDetail/ItemFeature';
import MainData from '@/src/components/UI/HouseDetail/MainData';
import MapViewSection from '@/src/components/UI/HouseDetail/MapViewSection';
import FullPage from '@/src/components/UI/FullPage';
import CopyIcon from '@/src/components/UI/CopyIcon';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import Link from 'next/link';
import React, { useMemo, useState } from 'react';
import { areaLabelFormatter, chineseNumeralFormatter, dollarLabelFormatter } from '@/src/libs/number-helper';
import Head from 'next/head';
import { isServer } from '@/src/libs/is-next-server';
import { IListingInBuyPage, Props } from './HouseDetailContainerProps';
import { getBuildingCompleteDate, getFloorInformation, totalSizeFormatter } from '@/utils/dataConvertor';
import { BuildingShowcaseDetailsEntity } from '@/swagger';
import classNames from 'classnames';
import { isSet } from '@/utils/formatChecker';
import Icon from '@/public/icon';
import Gallery, { GallerySection } from '@/src/components/feature/Gallery';
import NavBar from '@/src/components/UI/HouseDetail/NavBar';
import Badge from '@/src/components/UI/Badge';
import serializer from '@/src/libs/serializer';
import Flexbox from '@/components/common/Flexbox';
import clsx from 'clsx';
import AgentSection from '@/src/components/UI/HouseDetail/AgentSection';
import { getContactCTAText, getContactFormPath } from '@/pages/building/[...entity]';
import { getLayout } from '@/src/helper/listing';

const cropperCss = `:root{--swiper-navigation-size:44px}.swiper-button-next,.swiper-button-prev{position:absolute;top:50%;width:calc(var(--swiper-navigation-size)/ 44 * 27);height:var(--swiper-navigation-size);margin-top:calc(0px - (var(--swiper-navigation-size)/ 2));z-index:10;cursor:pointer;display:flex;align-items:center;justify-content:center;color:var(--swiper-navigation-color,var(--swiper-theme-color))}.swiper-button-next.swiper-button-disabled,.swiper-button-prev.swiper-button-disabled{opacity:.35;cursor:auto;pointer-events:none}.swiper-button-next:after,.swiper-button-prev:after{font-family:swiper-icons;font-size:var(--swiper-navigation-size);text-transform:none!important;letter-spacing:0;text-transform:none;font-variant:initial;line-height:1}.swiper-button-prev,.swiper-container-rtl .swiper-button-next{left:10px;right:auto}.swiper-button-prev:after,.swiper-container-rtl .swiper-button-next:after{content:'prev'}.swiper-button-next,.swiper-container-rtl .swiper-button-prev{right:10px;left:auto}.swiper-button-next:after,.swiper-container-rtl .swiper-button-prev:after{content:'next'}.swiper-button-next.swiper-button-white,.swiper-button-prev.swiper-button-white{--swiper-navigation-color:#ffffff}.swiper-button-next.swiper-button-black,.swiper-button-prev.swiper-button-black{--swiper-navigation-color:#000000}.swiper-button-lock{display:none}`;
const ModalPopup = dynamic(() => import('@/src/components/UI/HouseDetail/ModalPopup'), {
  loading: () => <div />,
  ssr: false,
});
// const NavBar = dynamic(() => import('@/src/components/UI/HouseDetail/NavBar'), {
//   loading: () => <div />,
//   ssr: false,
// });

const HouseDetailContainer = ({ detailData, agent, listingUrl, building }: Props) => {
  const [showLinePopup, setShowLinePopup] = useState(false);
  const [showCallMeBackForm, setShowCallMeBackForm] = useState(false);

  return (
    <FullPage
      hasPadding={false}
      sid={detailData?.sid}
      headerRight={['favorite', 'share']}
      title={detailData?.title ?? ''}
    >
      <section id={'house-info'} />
      <div className={'flex justify-center flex-col relative'}>
        <NavBar />
        <div className={'flex flex-1 flex-row px-8 w-full max-w-screen-lg  justify-center  self-center'}>
          <LeftComponent detailData={detailData} />
          <RightComponent detailData={detailData} listingUrl={listingUrl} building={building} />
        </div>
      </div>
      <ModalPopup
        agent={agent}
        setShowCallMeBackForm={setShowCallMeBackForm}
        setShowLinePopup={setShowLinePopup}
        showLinePopup={showLinePopup}
        showCallMeBackForm={showCallMeBackForm}
        needAddress={false}
        listingId={detailData.id}
      />
    </FullPage>
  );
};

const LeftComponent = ({ detailData }: { detailData: IListingInBuyPage }) => {
  const trafficEnv = useMemo(
    () => ({
      school: detailData.traffic_env_school,
      recreation: detailData.traffic_env_recreation,
      hospital: detailData.traffic_env_hospital,
      supermarket: detailData.traffic_env_supermarket,
      traffic: detailData.traffic_env_traffic,
      corporate: detailData.traffic_env_corporate,
    }),
    [
      detailData.traffic_env_school,
      detailData.traffic_env_recreation,
      detailData.traffic_env_hospital,
      detailData.traffic_env_supermarket,
      detailData.traffic_env_traffic,
      detailData.traffic_env_corporate,
    ]
  );

  const imageSections = useMemo(() => {
    return detailData.listing_image.reduce(
      (acc, cur) => {
        if (cur.tag === 'layout') {
          acc[1].images.push(cur.watermark_image ?? cur.original_image ?? '');
        } else {
          acc[0].images.push(cur.watermark_image ?? cur.original_image ?? '');
        }
        return acc;
      },
      [
        { key: 'default', text: undefined, images: [] },
        { key: 'layout', text: '格局圖', images: [], icon: <Icon.FloorPlanIcon /> },
      ] as Array<GallerySection>
    );
  }, [detailData.listing_image]);

  const videos = useMemo(
    () => detailData.listing_video.map((video) => video.video_url ?? ''),
    [detailData.listing_video]
  );

  return (
    <div className={'flex w-2/3 flex-col'}>
      <div className={'relative -mx-4 sm:mx-0 sm:mb-4'}>
        <Gallery imageSections={imageSections} videos={videos} />
        <Badge state={serializer(detailData.listing_state)} status={detailData.status!} sold_at={detailData.sold_at} />
      </div>
      <Caution className={'sm:block hidden sm:visible invisible'} />
      <MainData detailData={detailData} />
      <ItemFeature
        content={detailData.feature_content ?? ''}
        tags={detailData.feature_tags ?? []}
        hashtags={detailData.listing_hashtag}
      />
      <MapViewSection
        sid={detailData.sid}
        //@ts-ignore
        items={trafficEnv}
        country={detailData.country!}
        center={{
          //@ts-ignore
          lat: parseFloat(detailData.location_latitude ?? 0),
          //@ts-ignore
          lng: parseFloat(detailData.location_longitude ?? 0),
        }}
      />
      <HomeLoanCalculation price={detailData.price ?? 0} country={detailData.country!} className={'mb-9 pt-9'} />
    </div>
  );
};

export const Caution: React.FC<{ className?: string }> = ({ className }) => {
  return (
    <p className={classNames(className, 'text-xs text-gray500 font-medium')}>
      本物件照片中之相關裝飾或其他物品，僅係提供買方做為參考之用，並不隨同該物件買賣或贈與。於簽訂相關買賣契約前，請您詳細確認附隨買賣之設備或家飾、贈品。
    </p>
  );
};

const RightComponent = ({
  detailData,
  listingUrl,
  building,
}: {
  detailData: IListingInBuyPage;
  listingUrl: string;
  building: BuildingShowcaseDetailsEntity | null;
}) => {
  // const t = useT('agents');
  const { price, dealPrice, title, country, sid } = detailData;

  const houseStatusData = useMemo(() => {
    return [
      dealPrice
        ? {
            name: '成交價',
            content: `${chineseNumeralFormatter(dealPrice ?? 0, country!)}${dollarLabelFormatter(country!)}`,
          }
        : undefined,
      { name: '開價', content: `${chineseNumeralFormatter(price ?? 0, country!)}${dollarLabelFormatter(country!)}` },
      {
        name: '格局',
        content: getLayout(detailData, detailData.floor_plan_type),
      },
      //@ts-ignore
      {
        name: areaLabelFormatter(country!),
        content: totalSizeFormatter(detailData.detail_total_area_size, detailData.country, 'inline'),
      },
      { name: '樓層', content: getFloorInformation(detailData) },
      {
        name: '完工時間',
        content: getBuildingCompleteDate(
          detailData.year_of_building_completion,
          detailData.month_of_building_completion
        ),
        hide: !detailData.year_of_building_completion && !detailData.month_of_building_completion,
      },
    ].filter(isSet);
  }, [detailData]);

  return (
    <>
      <Head>
        <style>{cropperCss}</style>
      </Head>
      <div className={'flex w-1/3 pl-6 flex-col bg-white'}>
        <div className={'flex flex-col w-full'}>
          <div className={'flex flex-row justify-between items-center'}>
            <div className={'flex flex-row justify-start my-1 items-center'}>
              <p className='text-lg leading-loose font-medium text-dark-grey uppercase text-gray900'>{title}</p>
              {detailData.display_building_project && detailData.building_project !== '' && (
                <>
                  <div className={'w-px bg-gray700 mx-3'} style={{ height: '1rem' }} />
                  <p className='text-lg leading-loose font-medium text-dark-grey uppercase text-gray900'>
                    {detailData.building_project}
                  </p>
                </>
              )}
            </div>
            <div className={'flex flex-row items-center invisible md:visible'}>
              <AddFavoriteButton sid={detailData.sid} />
              {!isServer() ? (
                <CopyIcon text={listingUrl} />
              ) : (
                <div
                  data-tip='複製連結'
                  className={'w-12 h-12 hover:bg-gray100 flex items-center justify-center -mr-3'}
                >
                  <Image width={24} height={24} className={'w-6 h-6 '} src={'/icons-share-filled.png'} />
                </div>
              )}
            </div>
          </div>
          <Flexbox
            align={'center'}
            justify={'between'}
            className={'text-sm leading-normal uppercase text-gray700 py-2'}
          >
            <p>{detailData.display_address}</p>
            <p>{detailData.sid}</p>
          </Flexbox>
          <div className={'flex flex-col w-full'}>
            {houseStatusData.map((item, idx) => {
              return (
                <ItemDescription
                  key={`ItemDescription-${idx}-${item.name}-${item.content}`}
                  name={item.name}
                  value={item.content ?? ''}
                  hide={item.hide}
                />
              );
            })}
            {building && (
              <ItemDescription
                name={'建案資訊'}
                value={
                  <Link href={`/building/${building.sid}`} passHref>
                    <a className={'text-fb-blue underline'}>了解建案</a>
                  </Link>
                }
              />
            )}
          </div>
        </div>
        <div className={clsx('flex', 'mt-8 w-full', 'sticky top-16')}>
          <AgentSection
            sid={sid}
            title={title}
            className={'w-full'}
            contactFormPath={getContactFormPath(detailData.buildingId)}
            ctaText={getContactCTAText(detailData.buildingId)}
            ctaAction={'call'}
          />
        </div>
      </div>
    </>
  );
};

const ItemDescription = ({ name, value, hide }: { name: string; value: React.ReactNode; hide?: boolean }) => {
  if (hide) return null;

  return (
    <div className={'w-full flex flex-row justify-between items-start py-3 border-b  border-gray300'}>
      <p className='leading-normal tracking-wide uppercase text-gray700 '>{name}</p>
      <p className='leading-normal tracking-wide text-dark-grey uppercase text-gray900 whitespace-pre text-right'>
        {value}
      </p>
    </div>
  );
};

export default HouseDetailContainer;
